import {TEST_IDS} from '@github-ui/issue-body/constants/test-ids'
import {IssuesLoadingSkeleton} from '@github-ui/issues-loading-skeleton'
import {type QueryKey, useQueryClient} from '@github-ui/react-query'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {ChevronDownIcon, ChevronUpIcon, ProjectCheckIcon, ProjectTemplateIcon, TableIcon} from '@primer/octicons-react'
import {IconButton, Link, Truncate} from '@primer/react'
import {useCallback, useMemo, useSyncExternalStore} from 'react'

import {LABELS} from '../../constants/labels'
import {useClearProjectItemFieldValueMutation} from '../../mutations/clear-project-item-field-value'
import {useUpdateProjectItemFieldValueMutation} from '../../mutations/update-project-item-field-value'
import {projectItemSectionFieldListQueryOptions} from './fetch-project-item-section-field-list'
import {
  SingleSelectField,
  type SingleSelectFieldConfigFragment,
  type SingleSelectFieldFragment,
} from './fields/SingleSelectField'
import type {ProjectItemSectionViewNode} from './project-item-section-fields-query-types'
import styles from './ProjectItemSectionView.module.css'

const AUTO_ASSIGN_AGENT_TRIGGER_TYPE = 'auto_assign_agent'
const GET_PROJECT_ITEMS_ACTION_TYPE = 'get_project_items'
const MEMEX_WORKFLOWS_DATA_ID = 'memex-workflows-data'
const JSON_ISLAND_ROOT_ID = 'json-island-root'

const EMPTY_AUTO_ASSIGN_WORKFLOW_OPTION_IDS: ReadonlySet<string> = new Set<string>()

// useSyncExternalStore helpers: the JSON island contents do not change after
// page load, so subscribing is a no-op and the "server" snapshot is empty.
function subscribeAutoAssignWorkflows(): () => void {
  return () => {}
}

function getEmptyAutoAssignWorkflowOptionIds(): ReadonlySet<string> {
  return EMPTY_AUTO_ASSIGN_WORKFLOW_OPTION_IDS
}

type MemexWorkflowAction = {
  actionType?: string
  arguments?: {
    fieldId?: number
    fieldOptionId?: string
  }
}

type MemexWorkflow = {
  triggerType?: string
  enabled?: boolean
  actions?: MemexWorkflowAction[]
}

function fetchMemexWorkflowsData(): MemexWorkflow[] {
  if (typeof document === 'undefined') return []

  const root = document.getElementById(JSON_ISLAND_ROOT_ID)
  const jsonIsland = root
    ? root.querySelector(`:scope > script#${MEMEX_WORKFLOWS_DATA_ID}`)
    : document.getElementById(MEMEX_WORKFLOWS_DATA_ID)

  if (!(jsonIsland instanceof HTMLScriptElement) || !jsonIsland.textContent) return []

  try {
    const workflows: unknown = JSON.parse(jsonIsland.textContent)
    return Array.isArray(workflows) ? (workflows as MemexWorkflow[]) : []
  } catch {
    return []
  }
}

function getAutoAssignWorkflowOptionIds(statusFieldDatabaseId: number | null | undefined): ReadonlySet<string> {
  if (statusFieldDatabaseId == null) return EMPTY_AUTO_ASSIGN_WORKFLOW_OPTION_IDS

  return fetchMemexWorkflowsData().reduce((optionIds, workflow) => {
    if (workflow.triggerType !== AUTO_ASSIGN_AGENT_TRIGGER_TYPE || !workflow.enabled) return optionIds

    const sourceAction = workflow.actions?.find(action => action.actionType === GET_PROJECT_ITEMS_ACTION_TYPE)
    if (!sourceAction?.arguments?.fieldOptionId || sourceAction.arguments.fieldId !== statusFieldDatabaseId) {
      return optionIds
    }

    optionIds.add(sourceAction.arguments.fieldOptionId)
    return optionIds
  }, new Set<string>())
}

export type ProjectItemSectionViewProps = {
  projectItem: ProjectItemSectionViewFragment
  isPending: boolean
  open: boolean
  setOpen: (isOpen: boolean) => void
  onIssueUpdate: () => void
  isSelectedProject: boolean
  isProjectClosed: boolean
  owner: string
  repo: string
  issueNumber: number
  fieldValueQueryKeys?: readonly QueryKey[]
}

type ProjectItemSectionViewProjectNode = Pick<
  ProjectItemSectionViewNode['project'],
  'id' | 'title' | 'template' | 'viewerCanUpdate' | 'url'
> & {
  field:
    | (SingleSelectFieldConfigFragment &
        Pick<
          Extract<
            NonNullable<ProjectItemSectionViewNode['project']['field']>,
            {__typename: 'ProjectV2SingleSelectField'}
          >,
          'databaseId'
        >)
    | null
}

// RELAY-CLEANUP(ProjectsSectionQuery): RelayCompat widens nullable fields so the still-Relay parent
// data is assignable. Remove when ProjectsSectionQuery leaves Relay.
export type ProjectItemSectionViewFragment = RelayCompat<
  Pick<ProjectItemSectionViewNode, 'id' | 'isArchived'> & {
    project: ProjectItemSectionViewProjectNode
    fieldValueByName: SingleSelectFieldFragment | null
  }
>

export function ProjectItemSectionView({
  projectItem: {id, project, fieldValueByName, isArchived},
  isPending,
  setOpen,
  open,
  onIssueUpdate,
  isSelectedProject,
  isProjectClosed,
  owner,
  repo,
  issueNumber,
  fieldValueQueryKeys,
}: ProjectItemSectionViewProps) {
  const queryClient = useQueryClient()
  const fieldListQueryOptions = projectItemSectionFieldListQueryOptions(owner, repo, issueNumber, id)
  const clearProjectItemFieldValue = useClearProjectItemFieldValueMutation({
    owner,
    repo,
    issueNumber,
    projectItemId: id,
    queryKey: fieldListQueryOptions.queryKey,
    additionalQueryKeys: fieldValueQueryKeys,
    relayFieldName: project.field?.name ?? undefined,
    onCompleted: onIssueUpdate,
  })
  const updateProjectItemFieldValue = useUpdateProjectItemFieldValueMutation({
    owner,
    repo,
    issueNumber,
    projectItemId: id,
    queryKey: fieldListQueryOptions.queryKey,
    additionalQueryKeys: fieldValueQueryKeys,
    relayFieldName: project.field?.name ?? undefined,
    onCompleted: onIssueUpdate,
  })

  const prefetchFieldList = useCallback(() => {
    if (!open && !isPending) {
      void queryClient.prefetchQuery(fieldListQueryOptions)
    }
  }, [fieldListQueryOptions, isPending, open, queryClient])

  const projectIcon = isProjectClosed ? (
    <ProjectCheckIcon className={styles.projectIcon} />
  ) : project.template ? (
    <ProjectTemplateIcon className={styles.projectIcon} aria-hidden="true" />
  ) : (
    <TableIcon className={styles.projectIcon} aria-hidden="true" />
  )
  const statusFieldDatabaseId = project.field?.databaseId
  // Read the JSON island via useSyncExternalStore so the server renders an
  // empty set (matching the SSR markup) while the client renders the parsed
  // workflow set without a hydration mismatch warning.
  const clientSnapshot = useMemo(() => getAutoAssignWorkflowOptionIds(statusFieldDatabaseId), [statusFieldDatabaseId])
  const autoAssignWorkflowOptionIds = useSyncExternalStore(
    subscribeAutoAssignWorkflows,
    () => clientSnapshot,
    getEmptyAutoAssignWorkflowOptionIds,
  )

  return (
    <div className={styles.ProjectItemSectionViewContainer}>
      {isSelectedProject ? (
        <span className={styles.projectTitleText}>
          {projectIcon}
          <span className={styles.truncatedProjectTitle}>{project.title}</span>
          {isProjectClosed && <span className="sr-only">Closed</span>}
        </span>
      ) : (
        <div className={styles.projectLinkContainer}>
          <Link href={project.url} tabIndex={0} className={styles.projectLink}>
            {projectIcon}
            <Truncate title={project.title} data-testid={TEST_IDS.projectTitle} className={styles.Truncate}>
              {project.title}
            </Truncate>
            {isProjectClosed && <span className={styles.closedLabel}>Closed</span>}
          </Link>
        </div>
      )}
      <div className={styles.statusFieldContainer}>
        {isPending ? (
          <span className={styles.statusFieldLoading}>
            <IssuesLoadingSkeleton height="sm" width="25%" />
            <IssuesLoadingSkeleton height="sm" width="50%" />
          </span>
        ) : (
          project.field &&
          fieldValueByName !== undefined && (
            <SingleSelectField
              viewerCanUpdate={project.viewerCanUpdate && !isArchived}
              itemId={id}
              projectId={project.id}
              field={project.field}
              value={fieldValueByName as SingleSelectFieldFragment | null}
              onIssueUpdate={onIssueUpdate}
              onClearFieldValue={clearProjectItemFieldValue}
              onUpdateFieldValue={updateProjectItemFieldValue}
              isStatusField
              autoAssignWorkflowOptionIds={autoAssignWorkflowOptionIds}
            />
          )
        )}
        <IconButton
          className={styles.projectFieldsToggle}
          variant="invisible"
          disabled={isPending}
          onClick={() => {
            setOpen(!open)
          }}
          onPointerEnter={prefetchFieldList}
          size="small"
          icon={!open ? ChevronDownIcon : ChevronUpIcon}
          aria-label={!open ? LABELS.showMoreProjectFields : LABELS.hideMoreProjectFields}
          aria-expanded={open}
        />
      </div>
    </div>
  )
}
